import React, { useState } from 'react';
import { AlertTriangleIcon, PrinterIcon } from 'lucide-react';
import type { CustodyCase } from '../../../types/case';
import { useStore } from '../../../contexts/StoreContext';
import { Modal } from '../../shared/Modal';
import { ReleaseLetterDoc } from '../../print/ReleaseLetterDoc';
import { bn } from '../../../utils/bn';
type CopySet = 'original' | 'office' | 'all';
const COPIES: Record<CopySet, {
  label: string;
  labels: (string | undefined)[];
}> = {
  original: {
    label: 'শুধু মূল কপি',
    labels: [undefined]
  },
  office: {
    label: 'মূল + অফিস কপি',
    labels: ['মূল কপি', 'অফিস কপি']
  },
  all: {
    label: 'মূল + অফিস + ডাম্পিং স্টেশন কপি',
    labels: ['মূল কপি', 'অফিস কপি', 'ডাম্পিং স্টেশন কপি']
  }
};

/** Prints many release letters in one job — one A4 page per letter (and per copy). */
export function BatchPrintModal({
  open,
  onClose,
  letters
}: {
  open: boolean;
  onClose: () => void;
  letters: CustodyCase[];
}) {
  const {
    config
  } = useStore();
  const [copies, setCopies] = useState<CopySet>('original');
  const [order, setOrder] = useState<'pcr' | 'station'>('station');
  const drafts = letters.filter(c => c.letter?.status !== 'issued');
  const sorted = [...letters].sort((a, b) => order === 'pcr' ? a.pcr.localeCompare(b.pcr) : (a.station?.station ?? a.assignedStation).localeCompare(b.station?.station ?? b.assignedStation) || a.pcr.localeCompare(b.pcr));
  const pages = sorted.length * COPIES[copies].labels.length;
  return <Modal open={open} onClose={onClose} title={`অবমুক্তি পত্র ব্যাচ প্রিন্ট — ${bn(letters.length)}টি পত্র`} size="xl" footer={<>
        <span className="mr-auto self-center text-sm text-ink-muted" data-id="element-89" data-mp="204nvnslpuuar">মোট {bn(pages)} পৃষ্ঠা (A4)</span>
        <button className="btn btn-outline" onClick={onClose} data-id="element-90" data-mp="254aag6h70ilo">বন্ধ করুন</button>
        <button className="btn btn-primary" disabled={!letters.length} onClick={() => window.print()} data-id="element-91" data-mp="2a3dgnu8wsuv8"><PrinterIcon className="h-4 w-4" data-id="element-92" data-mp="2e4fi8hqatj0d" /> ব্যাচ প্রিন্ট</button>
      </>} data-id="element-88" data-mp="1qda2e2kcyqq2">
      <div className="no-print mb-4 grid gap-4 rounded-xl border border-line bg-canvas p-4 md:grid-cols-2" data-id="element-93" data-mp="2e2m64mwx26kz">
        <fieldset data-id="element-94" data-mp="exkvz44wtcqy">
          <legend className="label" data-id="element-95" data-mp="a141ztb1445y">কপি সংখ্যা</legend>
          <div className="space-y-1.5" data-id="element-96" data-mp="a2oyhogwlx6g">
            {(Object.keys(COPIES) as CopySet[]).map(k => <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-ink" data-id="element-97" data-mp="nudbdtyz8poh">
                <input type="radio" name="bp-copy" className="h-4 w-4 accent-navy" checked={copies === k} onChange={() => setCopies(k)} data-id="element-98" data-mp="izfslvbwb1ye" /> {COPIES[k].label}
              </label>)}
          </div>
        </fieldset>
        <fieldset data-id="element-99" data-mp="16r4otmru8akm">
          <legend className="label" data-id="element-100" data-mp="16rmv7c87s569">সাজানোর ক্রম</legend>
          <div className="space-y-1.5" data-id="element-101" data-mp="16p0nnclbppo1">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink" data-id="element-102" data-mp="12p01t6vwac6y"><input type="radio" name="bp-ord" className="h-4 w-4 accent-navy" checked={order === 'station'} onChange={() => setOrder('station')} data-id="element-103" data-mp="38ihtqbtt2igo" /> ডাম্পিং স্টেশন অনুযায়ী</label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink" data-id="element-104" data-mp="34iz9zjdq0dff"><input type="radio" name="bp-ord" className="h-4 w-4 accent-navy" checked={order === 'pcr'} onChange={() => setOrder('pcr')} data-id="element-105" data-mp="2ue92kxzjsh3t" /> PCR নম্বর অনুযায়ী</label>
          </div>
        </fieldset>
        {drafts.length > 0 && <p className="flex items-start gap-2 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-700 md:col-span-2" data-id="element-106" data-mp="2klbsng8mg0gt">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-107" data-mp="2ap8dn9so87cg" />
            {bn(drafts.length)}টি পত্র এখনো ইস্যু হয়নি — এগুলো “খসড়া” জলছাপ ও নিষ্ক্রিয় QR সহ প্রিন্ট হবে এবং হস্তান্তরে গ্রহণযোগ্য নয়।
          </p>}
      </div>
      <div className="print-area bg-canvas p-2" data-id="element-108" data-mp="21pllzfpqufwl">
        {sorted.flatMap(c => COPIES[copies].labels.map(label => <ReleaseLetterDoc key={`${c.pcr}-${label ?? 'o'}`} c={c} config={config} inBatch copyLabel={label} data-id="element-109" data-mp="26phh0pggqx00" />))}
      </div>
    </Modal>;
}